import * as opentype from 'opentype.js';

import { CURATED_FONTS, DEFAULT_FONT_ID } from '../fonts/font-catalog';
import type { FontDefinition } from '../types';

const fontCache = new Map<string, opentype.Font>();
const fontLoadingPromises = new Map<string, Promise<opentype.Font>>();

/**
 * Loads a font by ID or URL and caches the parsed opentype.Font instance.
 */
export async function loadFontById(
  fontId: string,
  customFonts: FontDefinition[] = [],
): Promise<opentype.Font> {
  if (fontCache.has(fontId)) {
    return fontCache.get(fontId)!;
  }

  if (fontLoadingPromises.has(fontId)) {
    return fontLoadingPromises.get(fontId)!;
  }

  const allFonts = [...CURATED_FONTS, ...customFonts];
  const fontDef = allFonts.find((f) => f.id === fontId) || CURATED_FONTS[0];

  const promise = (async () => {
    try {
      if (fontDef.url) {
        const response = await fetch(fontDef.url);
        if (!response.ok) {
          throw new Error(`Failed to fetch font ${fontDef.name}: HTTP ${response.status}`);
        }
        const buffer = await response.arrayBuffer();
        const font = opentype.parse(buffer);
        fontCache.set(fontId, font);
        return font;
      }
      throw new Error(`Font ${fontId} has no URL or buffer`);
    } catch (err) {
      console.error(`[FontLoader] Error loading font ${fontId}, falling back to default`, err);
      if (fontId !== DEFAULT_FONT_ID) {
        return loadFontById(DEFAULT_FONT_ID, customFonts);
      }
      throw err;
    } finally {
      fontLoadingPromises.delete(fontId);
    }
  })();

  fontLoadingPromises.set(fontId, promise);
  return promise;
}

/**
 * Parses user-uploaded font file (TTF / OTF / WOFF)
 */
export async function parseCustomFontFile(file: File): Promise<{
  fontDef: FontDefinition;
  font: opentype.Font;
}> {
  const buffer = await file.arrayBuffer();
  const font = opentype.parse(buffer);

  const familyName = font.names.fontFamily?.en || file.name.replace(/\.[^/.]+$/, '');
  const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const fontDef: FontDefinition = {
    id,
    name: familyName,
    family: familyName,
    category: 'custom' as any,
    isCustom: true,
    designer: font.names.designer?.en || 'Custom Font',
  };

  fontCache.set(id, font);
  return { fontDef, font };
}
